import type { NeighborhoodListItem } from '@loci/validation';

import { CLT_NEIGHBORHOOD_SLUGS, getNeighborhoodNameFromSlug } from '@/lib/neighborhoods/clt-slugs';

/**
 * Unit-viewBox rectangle used when the neighborhood boundary fetch fails
 * (WEB-648). Intentionally a plain 4-corner glyph — consumers that need a
 * *real* outline must assert a path with more than one L-segment so they do
 * not silently accept this fallback.
 *
 * Safe to import from client components (no server-only secrets).
 */
export const STATIC_FOOTPRINT_FALLBACK_PATH = 'M 0.15 0.15 L 0.85 0.15 L 0.85 0.85 L 0.15 0.85 Z';

/** Failure-path SVG `d` for hoodmarks / hero glyphs when boundaries are unavailable. */
export function getStaticFootprintFallbackPath(): string {
  return STATIC_FOOTPRINT_FALLBACK_PATH;
}

/**
 * Static fallback for the marketing-city `/neighborhoods` index.
 *
 * `fetchNeighborhoodList` fails closed to `[]` on any API error or empty
 * response (cold deploy, backend outage). Without a fallback the page renders
 * a blank "No neighborhoods to show" body under a header that still describes
 * a populated city — a contradiction a visitor can screenshot. This helper
 * returns every canonical CLT neighborhood so the grid can never go blank.
 *
 * WEB-813: this returns whatever `CLT_NEIGHBORHOOD_SLUGS` holds — **23** since
 * PIP-178, not the 7 an earlier version of this comment claimed. The index
 * subhead now derives its cardinal from the rendered card count instead of
 * asserting a fixed one, so the live and fail-closed paths both stay truthful
 * at any list length.
 *
 * **No fabricated visitor-facing metrics.** `blurbTeaser` is empty and
 * `walkScore` / `peakHourLabel` are null. `eventsThisWeek` is `0` only
 * because `NeighborhoodListItem` requires a number — the index does not
 * paint that placeholder. Week counts come from their own request and stay
 * off the card when that request fails (WEB-1227). `position` is 1-based
 * structural ordering (matches the canonical order in `clt-slugs.ts`), not
 * invented metric data.
 *
 * Slugs + display names are a *derived view* of `clt-slugs.ts` — the single
 * source of truth — never re-encoded here. Per CLAUDE.md's
 * no-realistic-fake-data rule this is safe: the seven slugs are real public
 * Charlotte neighborhood names, not fabricated events.
 *
 * Intended for server components (marketing index). The footprint path
 * helpers above are the client-safe failure path for WEB-648 glyphs.
 */
export function getStaticNeighborhoodFallback(): NeighborhoodListItem[] {
  return CLT_NEIGHBORHOOD_SLUGS.map((slug, index) => ({
    slug,
    name: getNeighborhoodNameFromSlug(slug) ?? slug,
    position: index + 1,
    blurbTeaser: '',
    walkScore: null,
    peakHourLabel: null,
    eventsThisWeek: 0,
  }));
}
